import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { date, LANG_NAMES } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';

/** CM-2 Notice list (wireframe W3). */

interface NoticeVersion {
  version: number;
  title: string;
  purposeIds: string[];
  status: 'draft' | 'published' | 'superseded';
  publishedAt: string | null;
  languages: string[];
}

interface Notice {
  id: string;
  key: string;
  currentVersion: number;
  versions: NoticeVersion[];
}

interface Purpose { id: string; status: string; current: { name: string } }

export default function Notices() {
  const writable = can(getUser(), 'notices:edit');
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const notices = useResource<{ notices: Notice[] }>(() => api.get('/v1/notices'));
  const purposes = useResource<{ purposes: Purpose[] }>(() => api.get('/v1/purposes?status=active'));

  async function openDraft(notice: Notice) {
    const latest = notice.versions[notice.versions.length - 1]!;
    if (latest.status === 'draft') {
      navigate(`/notices/${notice.id}/versions/${latest.version}`);
      return;
    }
    try {
      const res = await api.post<{ version: NoticeVersion }>(`/v1/notices/${notice.id}/versions`);
      toast(`Draft v${res.version.version} opened, seeded from v${latest.version}.`, 'ok');
      navigate(`/notices/${notice.id}/versions/${res.version.version}`);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead
        title="Notices"
      >
        {writable && <button className="primary" onClick={() => setCreating(true)}>New notice</button>}
      </PageHead>

      <ErrorNote error={notices.error} />
      {notices.loading && !notices.data && <Loading what="Loading notices" />}

      {notices.data && (notices.data.notices.length === 0 ? (
        <Empty>No notices.</Empty>
      ) : (
        notices.data.notices.map((n) => (
          <Card key={n.id} title={`${n.versions[0]?.title ?? n.key}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span className="small muted">
                {n.versions.some((v) => v.status === 'published') ? 'In force' : 'Not yet published'}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                {writable && (
                  <button className="small" onClick={() => openDraft(n)}>
                    {n.versions[n.versions.length - 1]?.status === 'draft' ? 'Continue draft' : 'New version'}
                  </button>
                )}
              </span>
            </div>

            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Languages</th>
                    <th>Purposes</th>
                    <th>Published</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {[...n.versions].reverse().map((v) => (
                    <tr key={v.version}>
                      <td>Version {v.version}</td>
                      <td>
                        {v.languages.length
                          ? v.languages.map((l) => (
                              <span className="chip" key={l} title={LANG_NAMES[l] ?? l}>{l}</span>
                            ))
                          : <span className="muted small">none yet</span>}
                      </td>
                      <td className="small">{v.purposeIds.length}</td>
                      <td className="small">{date(v.publishedAt)}</td>
                      <td>
                        <Pill tone={v.status === 'published' ? 'ok' : v.status === 'draft' ? 'warn' : undefined}>
                          {v.status === 'draft' ? 'Draft' : v.status === 'published' ? 'Published' : 'Superseded'}
                        </Pill>
                      </td>
                      <td>
                        <Link className="btn small" to={`/notices/${n.id}/versions/${v.version}`}>
                          {v.status === 'draft' ? 'Edit' : 'View'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      ))}

      {creating && (
        <NewNotice
          purposes={purposes.data?.purposes ?? []}
          onClose={() => setCreating(false)}
          onCreated={(notice) => {
            setCreating(false);
            navigate(`/notices/${notice.id}/versions/1`);
          }}
        />
      )}
    </>
  );
}

function NewNotice({
  purposes, onClose, onCreated,
}: { purposes: Purpose[]; onClose: () => void; onCreated: (n: Notice) => void }) {
  const [title, setTitle] = useState('Customer data notice');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ notice: Notice }>('/v1/notices', { title, purposeIds: selected });
      onCreated(res.notice);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New notice"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={create} disabled={busy || !title || selected.length === 0}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </>
      }
    >
      <Field label="Title">
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <Field label="Purposes" help="Active purposes only.">
        {purposes.length === 0 ? (
          <div className="note warn">No active purposes.</div>
        ) : (
          purposes.map((p) => (
            <label key={p.id} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '5px 0', fontWeight: 400 }}>
              <input
                type="checkbox" style={{ width: 'auto' }}
                checked={selected.includes(p.id)}
                onChange={(e) =>
                  setSelected((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))
                }
              />
              {p.current.name}
            </label>
          ))
        )}
      </Field>

      <ErrorNote error={error} />
    </Modal>
  );
}

export type { Notice, NoticeVersion };
